/**
 * Minimal reactive store with subscribe/publish, selector support,
 * and microtask-batched notifications. Zero dependencies.
 */

export interface Store<T> {
  getState(): Readonly<T>
  setState(updater: Partial<T> | ((prev: T) => Partial<T>)): void
  subscribe(listener: (state: Readonly<T>) => void): () => void
  select<S>(selector: (state: T) => S): Selector<S>
}

export interface Selector<S> {
  get(): S
  subscribe(listener: (value: S) => void): () => void
}

export function createStore<T extends object>(
  initialState: T,
): Store<T> {
  let state: T = { ...initialState }
  const listeners = new Set<(state: Readonly<T>) => void>()
  let notifyScheduled = false

  function scheduleNotify(): void {
    if (notifyScheduled) return
    notifyScheduled = true
    queueMicrotask(() => {
      notifyScheduled = false
      const frozen = state
      for (const listener of listeners) {
        try {
          listener(frozen)
        } catch (err) {
          console.error('[store] listener threw:', err)
        }
      }
    })
  }

  function getState(): Readonly<T> {
    return state
  }

  function setState(updater: Partial<T> | ((prev: T) => Partial<T>)): void {
    const partial = typeof updater === 'function' ? updater(state) : updater
    const next = { ...state, ...partial }

    // Skip notify if nothing actually changed (shallow check)
    let changed = false
    for (const key of Object.keys(partial) as (keyof T)[]) {
      if (state[key] !== next[key]) {
        changed = true
        break
      }
    }

    if (!changed) return
    state = next
    scheduleNotify()
  }

  function subscribe(listener: (state: Readonly<T>) => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  function select<S>(selector: (state: T) => S): Selector<S> {
    return {
      get(): S {
        return selector(state)
      },
      subscribe(listener: (value: S) => void): () => void {
        let prev = selector(state)
        return subscribe((newState) => {
          const next = selector(newState)
          if (!Object.is(prev, next)) {
            prev = next
            listener(next)
          }
        })
      },
    }
  }

  return { getState, setState, subscribe, select }
}
